"use client";
import { Size } from "@/api/AppApi/common-interfaces/api-enums";
import { DiscountTypes } from "@/api/AppApi/services/home/interfaces/IProduct";
import { useCurrencyCalc } from "@/hooks/useCurrencyCalc";
import SvgBadge from "./SvgBadge";
import ProductTagBadge from "./ProductTagBadge";
import type { IResolvedProductTag } from "@/utils/functions/product-tags";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { formatDiscountPercent } from "@/utils/functions/discount";
interface Props {
  discount_amount: number;
  showNewBadge: boolean;
  size: Size;
  discount_type?: DiscountTypes;
  showDiscountBadge: boolean;
  isSaleLabel: boolean;
  discount?: number;
  outOfStock?: boolean;
  showSoldQuantityBadge: boolean;
  sold_quantity?: number;
  /** Theme-configured catalog tags assigned to this product; already resolved + coloured. */
  productTags?: IResolvedProductTag[];
  productId?: number | string;
}
const ProductBadge = ({
  discount_amount,
  size,
  showNewBadge,
  showDiscountBadge,
  discount_type,
  isSaleLabel,
  discount,
  outOfStock,
  showSoldQuantityBadge,
  sold_quantity,
  productTags,
  productId,
}: Props) => {
  const t = useTranslation();
  const { data } = useGetCompanyQuery();
  const { currency, getPriceString } = useCurrencyCalc({
    enable: data?.result?.theme?.settings?.enable_currencies_converter ?? true,
  });
  let discountText = isSaleLabel
    ? t.sale
    : discount_type === DiscountTypes.fixed
    ? `-${getPriceString(discount_amount)} ${currency?.symbol}`
    : (formatDiscountPercent(discount) ?? "");

  const large = size === Size.large;
  return (
    <div
      className="m-0 d-flex flex-column product-badges"
      style={{
        width: "fit-content",
        position: "absolute",
        top: 8,
        alignItems: "top",
        left: 0,
      }}
    >
      {showNewBadge && (
        <div className="p-0 new-badge">
          <SvgBadge text={t.new} large={large} />
        </div>
      )}
      {showSoldQuantityBadge && (
        <div className="p-0 sold-badge">
          <SvgBadge
            text={`${t.sold} ${sold_quantity} ${t.pcs}`}
            large={large}
          />
        </div>
      )}
      {showDiscountBadge && (
        <div className="p-0 discount-badge">
          <SvgBadge text={discountText} large={large} sale={isSaleLabel} />
        </div>
      )}
      {outOfStock && (
        <div className="p-0 out-of-stock-badge">
          <SvgBadge text={t.out_of_stock} large={large} sale={true} />
        </div>
      )}
      {!!productTags?.length && (
        <div
          className="product-tag-badges"
          id={productId ? `product-tag-badges-${productId}` : undefined}
        >
          {productTags.map((tag) => (
            <ProductTagBadge
              key={tag.id}
              tag={tag}
              productId={productId!}
              large={large}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductBadge;
